/**
 * Native → Web Event Handler System
 *
 * 네이티브에서 웹으로 전달되는 단방향 이벤트를 처리하는 시스템
 */

import type { NativeEventAction, NativeEventGroup, NativeEventHandler, NativeEventMessage } from './types'

// 이벤트 핸들러 저장소
type EventHandlerMap = Map<
  string, // `${group}:${action}`
  Set<NativeEventHandler<any, any>>
>

const eventHandlers: EventHandlerMap = new Map()

/**
 * 이벤트 핸들러 등록
 *
 * @param group 이벤트 그룹
 * @param action 이벤트 액션
 * @param handler 이벤트 핸들러 함수
 * @returns 해제 함수
 *
 * @example
 * ```typescript
 * const unsubscribe = addNativeEventListener('lifecycle', 'onAppStateChange', (data) => {
 *   console.log('App state:', data.state)
 * })
 *
 * // 나중에 해제
 * unsubscribe()
 * ```
 */
export function addNativeEventListener<TGroup extends NativeEventGroup, TAction extends NativeEventAction<TGroup>>(
  group: TGroup,
  action: TAction,
  handler: NativeEventHandler<TGroup, TAction>
): () => void {
  const key = `${group}:${String(action)}`

  if (!eventHandlers.has(key)) {
    eventHandlers.set(key, new Set())
  }

  const handlers = eventHandlers.get(key)!
  handlers.add(handler as NativeEventHandler<any, any>)

  // 해제 함수 반환
  return () => {
    const handlers = eventHandlers.get(key)
    if (handlers) {
      handlers.delete(handler as NativeEventHandler<any, any>)

      // 핸들러가 없으면 맵에서 제거
      if (handlers.size === 0) {
        eventHandlers.delete(key)
      }
    }
  }
}

/**
 * 특정 이벤트의 모든 핸들러 제거
 *
 * @param group 이벤트 그룹
 * @param action 이벤트 액션
 */
export function removeNativeEventListeners<TGroup extends NativeEventGroup>(
  group: TGroup,
  action: NativeEventAction<TGroup>
): void {
  const key = `${group}:${String(action)}`
  eventHandlers.delete(key)
}

/**
 * 모든 이벤트 핸들러 제거
 */
export function clearAllNativeEventListeners(): void {
  const count = eventHandlers.size
  eventHandlers.clear()
}

/**
 * 네이티브 이벤트 메시지 처리
 *
 * @param message 네이티브에서 전달된 이벤트 메시지
 */
export function handleNativeEvent(message: NativeEventMessage): void {
  const { group, action, data } = message
  const key = `${group}:${String(action)}`

  const handlers = eventHandlers.get(key)

  if (!handlers || handlers.size === 0) {
    return
  }

  // 모든 핸들러 실행
  handlers.forEach(handler => {
    try {
      handler(data)
    } catch (error) {
      console.error(`[Bridge:Events] Error in handler for ${key}:`, error)
    }
  })
}

/**
 * 등록된 이벤트 핸들러 상태 조회 (디버깅용)
 */
export function getNativeEventListenersStatus(): {
  count: number
  events: Array<{
    key: string
    handlerCount: number
  }>
} {
  const events = Array.from(eventHandlers.entries()).map(([key, handlers]) => ({
    key,
    handlerCount: handlers.size
  }))

  return {
    count: events.length,
    events
  }
}
